import React from 'react';
import TestResultDisplay from '../../common/TestResultDisplay';

// 测试项目接口
interface TestItem {
  name: string;
  passed: boolean;
  userAnswer?: string;
  correctAnswer?: string;
  details?: string;
}

// 测试组接口
interface TestGroup {
  title: string;
  subtitle?: string;
  score?: { current: number; total: number };
  items: TestItem[];
  passed: boolean;
  note?: string;
  hasLogicalError?: boolean;
  errorReason?: string;
}

interface MobilitySummaryDisplayProps {
  responses?: any; // 用户输入数据
  mobilityStatus?: 'PASS' | 'FAIL' | 'INCONCLUSIVE';
  completionMethod?: string;
  className?: string;
}

const MobilitySummaryDisplay: React.FC<MobilitySummaryDisplayProps> = ({ 
  responses,
  mobilityStatus,
  completionMethod,
  className = '' 
}) => {

  // 判断行动能力是否通过 - 基于传入的状态
  const mobilityPassed = mobilityStatus === 'PASS';
  
  // 判断测试是否被跳过
  const isTestSkipped = completionMethod === 'test_skipped' || completionMethod === 'skipped';
  
  // 从真实的responses数据中获取椅子起立测试数据
  const getRealMobilityData = () => {
    
    // 方式1: 从直接的completedRises字段获取
    if (responses?.completedRises !== undefined) {
      return {
        completedRises: responses.completedRises,
        timeUsed: responses.timeUsed || 14,
        skipReason: responses.skipReason
      };
    }
    
    // 方式2: 从chairStandTest嵌套对象获取
    if (responses?.chairStandTest) {
      return {
        completedRises: responses.chairStandTest.completedRises,
        timeUsed: responses.chairStandTest.timeUsed || 14,
        skipReason: responses.chairStandTest.skipReason
      };
    }
    
    // 方式3: 从testResults中椅子起立测试的rawUserAnswers获取 (对应AssessmentManager的数据结构)
    if (responses?.testResults) {
      const chairTest = responses.testResults.find((test: any) => test.name === "椅子站立测试" || test.name === "椅子起立测试");
      if (chairTest?.rawUserAnswers) {
        return {
          completedRises: chairTest.rawUserAnswers.completedRises,
          timeUsed: chairTest.rawUserAnswers.timeUsed || 14,
          skipReason: chairTest.rawUserAnswers.skipReason
        };
      }
      if (chairTest?.details) {
        return {
          completedRises: chairTest.details.completedRises,
          timeUsed: chairTest.details.timeUsed || 14,
          skipReason: chairTest.details.skipReason
        };
      }
    }
    
    // 方式4: 从assessmentData或domainResult中的testResults获取 (QuickTestResultPage的数据结构修复)
    // 这是关键修复：QuickTestResultPage应该传递完整的domainResult而不只是responses
    if ((responses as any)?.status && (responses as any)?.testResults) {
      const chairTest = (responses as any).testResults.find((test: any) => test.name === "椅子站立测试" || test.name === "椅子起立测试");
      if (chairTest?.rawUserAnswers) {
        return {
          completedRises: chairTest.rawUserAnswers.completedRises,
          timeUsed: chairTest.rawUserAnswers.timeUsed || 14,
          skipReason: chairTest.rawUserAnswers.skipReason
        };
      }
      if (chairTest?.details) {
        return {
          completedRises: chairTest.details.completedRises,
          timeUsed: chairTest.details.timeUsed || 14,
          skipReason: chairTest.details.skipReason
        };
      }
    }
    
    return null;
  };

  // 动态生成行动能力测试数据，根据不同情况显示详细信息
  const generateMobilityTestData = (): TestGroup[] => {
    const realData = getRealMobilityData();
    
    // 如果测试被跳过，显示跳过原因
    if (isTestSkipped) {
      const skipReason = realData?.skipReason || '安全考虑';
      return [
        {
          title: '椅子起立测试',
          passed: false,
          items: [
            {
              name: '测试状态',
              passed: false,
              userAnswer: '出于安全考虑跳过测试',
              details: `跳过原因: ${skipReason}`
            }
          ]
        }
      ];
    }

    // 使用真实数据或降级到状态显示
    const completedCount = realData?.completedRises !== undefined 
      ? realData.completedRises 
      : (mobilityPassed ? '数据不可用(通过)' : '数据不可用(失败)');
    const testTimeSeconds = realData?.timeUsed !== undefined 
      ? realData.timeUsed 
      : 14;
    const chairTestPassed = typeof completedCount === 'number' ? completedCount >= 5 : mobilityPassed;
    
    const testGroups: TestGroup[] = [
      {
        title: '椅子起立测试',
        passed: chairTestPassed,
        items: [
          {
            name: '完成次数',
            passed: chairTestPassed,
            userAnswer: typeof completedCount === 'number' ? `${completedCount}次` : completedCount
          },
          {
            name: '测试时间',
            passed: true,
            userAnswer: typeof testTimeSeconds === 'number' ? `${testTimeSeconds.toFixed(1)}秒` : '14.0秒'
          },
          {
            name: '测试结果',
            passed: chairTestPassed,
            userAnswer: chairTestPassed ? '通过' : '未达标'
          }
        ]
      }
    ];

    return testGroups;
  };

  const testGroups = generateMobilityTestData();
  const passedCount = testGroups.filter(group => group.passed).length;
  
  // 根据不同情况生成不同的副标题
  const getSubtitle = (): string => {
    if (isTestSkipped) {
      return '出于安全考虑跳过测试';
    }
    
    // ICOPE行动能力评估只有椅子起立测试一项
    if (passedCount > 0) {
      return '椅子起立测试通过 - 行动能力正常';
    } else {
      return '椅子起立测试未通过 - 需要关注';
    }
  };

  return (
    <TestResultDisplay
      title="行动能力评估结果"
      subtitle={isTestSkipped ? undefined : getSubtitle()}
      testGroups={testGroups}
      className={className}
    />
  );
};

export default MobilitySummaryDisplay;